import DOMPurify from 'dompurify';
import { OutreachBannerConfig } from '../types/campaign.types';

type Nullish<T = undefined> = T | null;

export type RecruiterDetailsResponse = {
    login_type: string;
    brandname: string;
    user_type: string;
    is_logged_in: boolean;
    shared_user: string[];
    domaintype: string;
    auth_message: string;
    is_beta_customer: boolean;
    companyLogoUrl: string;
    first_name: string;
    tracking_id: string;
    cremail: string;
    site_context: string;
    new_search_beta_customer: boolean;
    "foundit.in": string;
    subhchannel_id: string;
    sales_channel: string;
    last_name: string;
    recruiter_id: string;
    is_master_have_child: boolean;
    ip_address: string;
    services: string[];
    is_quick_jp: boolean;
    salesemail: string;
    rda_url: string;
    log_out_url: string;
    parent_id: string;
    company_name: string;
    v4srp_url: string;
    v4s_url: string;
    pagetitle: string;
    session_limit_exceeded: boolean;
    corp_id: string;
    channel_id: string;
    username: string;
  };

export function getUserDetails(): Nullish<RecruiterDetailsResponse> {
  // Check if user details exist in session storage
  const userDetailsString = sessionStorage.getItem("userDetails");
 
  if (userDetailsString) {
    // If user details exist, parse the JSON string and return the object
    return JSON.parse(userDetailsString);
  } else {
    // If user details do not exist, return userDetails;
    return null;
  }
}

export const defaultErrorMsg = {
  salaryRange : "Minimum salary should be less than or equal to Maximum salary."
}

// Define allowed style properties
const allowedStyles = ['font-weight', 'text-decoration', 'font-style'];

// Add a global hook to filter styles
DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
  if (data.attrName === 'style') {
    const filteredStyles = data.attrValue
      .split(';')
      .map(style => style.trim())
      .filter(style => {
        const [key] = style.split(':').map(s => s.trim());
        return allowedStyles.includes(key);
      })
      .join('; ');

    if (filteredStyles) {
      data.attrValue = filteredStyles;
    } else {
      data.keepAttr = false; // Remove empty style attribute
    }
  }
});

const sanitizeHtml = (pastedHtml: string): string => {
  return DOMPurify.sanitize(pastedHtml, {
    FORBID_ATTR: ['class', 'className'],
    FORBID_TAGS: ['input', 'iframe', 'button', 'a', 'img', 'svg'],
    ALLOWED_ATTR: ['style']
  });
};

export default sanitizeHtml;



export function getOutreachBannerMessage({
  level,
  isLimitReached,
  isExhausted,
  inventoryLevel,
}: OutreachBannerConfig) {
  if (!isLimitReached) return null;

  const isRecruiter = level === "recruiter";

  // 80% USED
  if (!isExhausted) {
    return {
      message: isRecruiter
        ? `You've used 80% of your ${inventoryLevel} recruiter outreach credits`
        : `Your account has used 80% of its ${inventoryLevel} outreach credits`,
      cta: "Review limit",
    };
  }

  // 100% EXHAUSTED
  return {
    message: isRecruiter
      ? "Your recruiter outreach credits has been fully exhausted"
      : "Your account total outreach credits has been fully exhausted",
    cta: "Upgrade limit",
  };
}

function capitalizeFirstChar(value: string): string {
  if (!value) return value;
  return value?.charAt(0)?.toUpperCase() + value?.toLowerCase()?.slice(1);
}


export function getCampaignUIStatus(
  backendStatus: string,
  backendSubStatus?: string,
) {
  const status = backendStatus?.toLowerCase();
  const subStatus = backendSubStatus?.toLowerCase();

  // Direct statuses
  if (
    status === "draft" ||
    status === "scheduled" ||
    status === "live" ||
    status === "in review" ||
    status === "rejected"
  ) {
    return { status: capitalizeFirstChar(backendStatus) };
  }

  const statusMap: Record<string, Record<string, string>> = {
    paused: {
      recruiter: "Paused",
      // blast: "Paused - By other blast campaign",
      account_disabled: "Paused - Account disabled",
      inactivity: "Paused - Inactivity",
    },

    auto_paused: {
      personal_mail_de_auth: "Auto Paused - Email not verified",
      insufficient_inventory: "Auto Paused - Low credits",
    },

    finished: {
      aborted_by_rec: "Aborted",
      aborted_internally: "Aborted - Technical error",
      inactivity: "Aborted - Inactivity",
      insufficient_inventory: "Auto Aborted - Low credits",
      all_email_sent: "Completed",
      personal_mail_de_auth: "Auto Aborted - Email not verified",
    },
  };

  return {
    status:
      statusMap[status]?.[subStatus ?? ""] ||
     (backendStatus === "auto_paused" ? "Auto Paused" : capitalizeFirstChar(backendStatus))
  };
}

